import { Box, Paper, Typography } from "@mui/material";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { getChartSliceColor } from "../../../utils/distinctColors";

export type StatusCardDatum = {
  statusName: string;
  count: number;
  pct: number;
};

type DealStatusBreakdownChartProps = {
  cards: StatusCardDatum[];
  totalDeals: number;
  selectedStatus: string | null;
  onSelectStatus: (statusName: string | null) => void;
  minHeight?: number;
};

export const DEAL_ANALYSIS_PANEL_HEIGHT = 600;

type ChartTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: StatusCardDatum & { fill?: string };
  }>;
};

function ChartTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: "relative",
        zIndex: 20,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#182737" }}>
        {item.statusName}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
        {item.count} deals · {Math.round(item.pct)}%
      </Typography>
    </Paper>
  );
}

export function DealStatusBreakdownChart({
  cards,
  totalDeals,
  selectedStatus,
  onSelectStatus,
  minHeight = DEAL_ANALYSIS_PANEL_HEIGHT,
}: DealStatusBreakdownChartProps) {
  if (cards.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: "#182737" }}>
        No status breakdown available for this month.
      </Typography>
    );
  }

  const chartData = cards.map((card, index) => ({
    ...card,
    fill: getChartSliceColor(index),
  }));

  const handleSliceClick = (statusName: string) => {
    onSelectStatus(selectedStatus === statusName ? null : statusName);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        minHeight,
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        background:
          "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography className="text-general-section-title" sx={{ mb: 0.25 }}>
          Status breakdown
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          Click a segment or row to filter deals
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.2fr 0.8fr" },
          gap: { xs: 1.5, lg: 1.5 },
          px: { xs: 1.5, md: 2 },
          py: 2,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: { xs: 220, lg: 280 },
            height: "100%",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              sx={{
                fontSize: 32,
                fontWeight: 700,
                color: "#182737",
                lineHeight: 1,
              }}
            >
              {totalDeals}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
              Total Deals
            </Typography>
          </Box>

          <Box sx={{ position: "relative", zIndex: 2, height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="count"
                  nameKey="statusName"
                  cx="50%"
                  cy="50%"
                  innerRadius="52%"
                  outerRadius="92%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0}
                  stroke="none"
                  strokeWidth={0}
                  onClick={(_, index) => {
                    const statusName = chartData[index]?.statusName;
                    if (statusName) handleSliceClick(statusName);
                  }}
                >
                  {chartData.map((entry) => {
                    const isSelected =
                      selectedStatus == null ||
                      selectedStatus === entry.statusName;
                    return (
                      <Cell
                        key={entry.statusName}
                        fill={entry.fill}
                        opacity={isSelected ? 1 : 0.28}
                        stroke="none"
                        style={{ cursor: "pointer", outline: "none" }}
                      />
                    );
                  })}
                </Pie>
                <Tooltip
                  content={<ChartTooltip />}
                  wrapperStyle={{ zIndex: 30, pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            minHeight: 0,
            overflowY: "auto",
            pr: 0.5,
            justifyContent: "center",
          }}
        >
          {chartData.map((card) => {
            const isSelected = selectedStatus === card.statusName;
            const isDimmed =
              selectedStatus != null && selectedStatus !== card.statusName;

            return (
              <Box
                key={card.statusName}
                onClick={() => handleSliceClick(card.statusName)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 0.75,
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: isSelected ? "#1B7A63" : "transparent",
                  backgroundColor: isSelected
                    ? "rgba(27, 122, 99, 0.08)"
                    : isDimmed
                      ? "transparent"
                      : "rgba(15, 23, 42, 0.02)",
                  cursor: "pointer",
                  transition:
                    "background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease",
                  opacity: isDimmed ? 0.55 : 1,
                  "&:hover": {
                    backgroundColor: isSelected
                      ? "rgba(27, 122, 99, 0.1)"
                      : "rgba(15, 23, 42, 0.04)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: card.fill,
                    flexShrink: 0,
                  }}
                />

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    title={card.statusName}
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#182737",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mb: 0.25,
                      lineHeight: 1.2,
                    }}
                  >
                    {card.statusName}
                  </Typography>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 999,
                      backgroundColor: "#E8EDF2",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${card.pct}%`,
                        height: "100%",
                        borderRadius: 999,
                        backgroundColor: card.fill,
                        minWidth: card.count > 0 ? 3 : 0,
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ textAlign: "right", flexShrink: 0, pl: 0.25 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#182737",
                      lineHeight: 1.1,
                    }}
                  >
                    {card.count}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                    {Math.round(card.pct)}%
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}
